import { cn } from '@noor/lib/cn';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';
import { FilterClearButton } from './filter-clear-button';
import { useFiltersContext } from './filters-context';

export interface FiltersSelectOption {
  label: string;
  value: string;
}

export interface FiltersSelectProps {
  paramKey: string;
  options: FiltersSelectOption[];
  /** Label for the "no filter" option. Selecting it removes the param. */
  allLabel?: string;
  /** Treat this parsed/default route value as the "no filter" option. */
  defaultValue?: string;
  /** Value displayed when the parameter is absent (for required selectors). */
  fallbackValue?: string;
  /** Hide the all/no-filter option for required selectors. */
  showAll?: boolean;
  /**
   * Muted "Label:" prefix rendered before the value — the Figma
   * "Сегмент: Все" pill. The value always reads (the "all" choice included),
   * so the prefix never dangles alone.
   */
  label?: string;
  placeholder?: string;
  'aria-label'?: string;
  className?: string;
}

// Sentinel for the "all / no filter" choice (Radix Select forbids empty values).
const ALL_VALUE = '__all__';

/** Single-select filter bound to a URL search param. */
export function FiltersSelect({
  paramKey,
  options,
  allLabel = 'All',
  defaultValue,
  fallbackValue,
  showAll = true,
  label,
  placeholder,
  className,
  ...aria
}: FiltersSelectProps) {
  const { getParam, setParam } = useFiltersContext();
  const raw = getParam(paramKey);
  const isDefaultValue = defaultValue !== undefined && raw === defaultValue;
  const current =
    raw === undefined ? (fallbackValue ?? ALL_VALUE) : isDefaultValue ? ALL_VALUE : raw;
  const hasValue = raw !== undefined && !isDefaultValue;
  const ariaLabel = aria['aria-label'] ?? placeholder ?? allLabel;

  return (
    // Hug the content like the Figma pill — no minimum width. The pill grows
    // with its label (so «Barcha kategoriyalar» (uz) is never clipped) and
    // only ellipsizes past `max-w-64`. Callers that want a floor pass their
    // own `min-w-*` via `className`; the prefix and the value stay glued
    // together on the left while the chevron alone pins to the right edge.
    <div className={cn('relative w-fit max-w-64', className)}>
      <Select
        value={current}
        onValueChange={(next) =>
          setParam(paramKey, showAll && next === ALL_VALUE ? undefined : next)
        }
      >
        <SelectTrigger
          className={cn(
            // Figma filter pill: h-10 white rounded-3xl, 14px medium black
            // value. `min-w-0` lets the value's `line-clamp-1` actually clamp
            // — without it the trigger's flex child keeps its intrinsic width
            // and the text overflows the pill instead of ellipsizing.
            // `justify-start` (overriding the base select's `justify-between`):
            // with three flex items — prefix, value, chevron — `justify-between`
            // would park the value mid-pill. Instead the prefix and the value
            // hug the left and only the chevron takes `ml-auto`, pinning
            // itself to the right edge when a caller-passed `min-w-*` leaves
            // free space inside the pill.
            'h-10 w-full min-w-0 justify-start rounded-[24px] border-filter-border bg-filter-bg font-medium text-filter-value-fg data-[placeholder]:font-normal [&>svg:last-child]:ml-auto',
            '*:data-[slot=select-value]:min-w-0',
            // Make room for the clear button + hide the chevron when a value is
            // set, so the "×" takes the chevron's place.
            hasValue && 'pr-9 [&>svg:last-child]:hidden',
          )}
          aria-label={ariaLabel}
        >
          {label ? (
            // `-mr-1` pulls the value to a word-space (4px) after the colon
            // while the trigger keeps its 8px rhythm toward the chevron.
            <span className="-mr-1 shrink-0 font-normal tracking-[-0.28px] text-filter-label-fg">
              {label}:
            </span>
          ) : null}
          <SelectValue placeholder={placeholder ?? allLabel} />
        </SelectTrigger>
        <SelectContent>
          {showAll ? <SelectItem value={ALL_VALUE}>{allLabel}</SelectItem> : null}
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasValue ? (
        <FilterClearButton
          onClear={() => setParam(paramKey, undefined)}
          aria-label={`${ariaLabel} — clear`}
          className="absolute top-1/2 right-3 -translate-y-1/2"
        />
      ) : null}
    </div>
  );
}
